// Core packages

// Third party packages

// Custom packages
import {
  DataTypeField,
  DataTypeFieldOptionsSource,
  DataTypeFieldSearch,
  DataTypeFieldType,
  DataTypeOnChangeHandler,
  DataTypeRequiredWhen,
  DataTypeRequiredWhenOperator,
} from "src/app/shared/interfaces/data-type.interface";

/**
 * Pure helpers of the data types editor: closed vocabularies, client side mirror of
 * the back-end validators, and the capability map that decides which attribute makes
 * sense on which field type.
 *
 * Nothing here talks to Angular: it is all plain data and pure functions, so the page
 * and the preview renderer can share it without dragging a service around.
 *
 * @since 3.0.0
 */

// BEGIN - Validation patterns (mirror of the back-end validators)

/** `dataTypeName` and `fields[].name`: a letter, then letters, digits or '_' */
export const DATA_TYPE_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,99}$/;

/** `dataTypeLabel`: alphanumeric first char, then the punctuation the codes use */
export const DATA_TYPE_LABEL_PATTERN = /^[A-Za-z0-9][A-Za-z0-9+*/\-_.,#() ]{0,49}$/;

/** A data type holds between 1 and 50 fields */
export const MIN_FIELDS = 1;
export const MAX_FIELDS = 50;

/** A closed list holds at most 100 options */
export const MAX_OPTIONS = 100;

/** Gap between two consecutive positions: sparse on purpose, like the back-end data */
export const POSITION_STEP = 100;

// END - Validation patterns

// BEGIN - Closed vocabularies
//
// Every list below is a CONTRACT with the back-end enum AND with front-end code that
// resolves the value at runtime. They are offered as closed selects: a value invented
// here is a 400, or worse a widget that silently renders nothing.

/** Widgets a field can be rendered as */
export const FIELD_TYPES: DataTypeFieldType[] = [
  "String",
  "Number",
  "Date",
  "DateNotAvailable",
  "Select",
  "SelectNew",
  "Hidden",
  "Autocomplete-Supplier",
];

/** Runtime providers of a `SelectNew` list */
export const OPTIONS_SOURCES: DataTypeFieldOptionsSource[] = [
  "conductorName",
  "nameInterventionArchiving",
  "suppliersName",
  "yearsOfStorage",
];

/** Remote search providers of an `Autocomplete-Supplier` */
export const SEARCH_SERVICES: DataTypeFieldSearch[] = ["suppliersService"];

/** Comparisons available to a conditional "required" rule */
export const REQUIRED_WHEN_OPERATORS: DataTypeRequiredWhenOperator[] = [
  "eq",
  "ne",
  "in",
  "nin",
];

/** Side effects the front-end knows how to run */
export const ON_CHANGE_HANDLERS: DataTypeOnChangeHandler[] = [
  "autoRenewalFromDuration",
];

// END - Closed vocabularies

// BEGIN - i18n conventions

/**
 * `fields[].label` is NOT a full i18n key: the archive resolves it as this prefix plus
 * the stored token, and falls back on the field `name` when the token is missing.
 *
 * `options[].label` instead IS a full key, stored complete.
 *
 * @see documentTypes.ts:getFormlyFields2, which is where the convention lives
 */
export const FIELD_LABEL_PREFIX = "properties.detail.documentArchive.fields.";

/** Label of the "not available" checkbox paired with a DateNotAvailable field */
export const DATE_NOT_AVAILABLE_LABEL_KEY =
  "properties.detail.documentArchive.notAvailable";

/** Suffix of the control the DateNotAvailable checkbox writes into */
export const DATE_NOT_AVAILABLE_SUFFIX = "chkNA";

// END - i18n conventions

// BEGIN - Capability map

/**
 * How the archive renderer treats `label` for a given type.
 *
 *  - `used`     : rendered, with the field name as fallback
 *  - `required` : rendered with NO fallback, so an empty label shows "…fields.undefined"
 *  - `ignored`  : the renderer always uses the field name, whatever is stored here
 *
 * Read off `getFormlyFields2`: this is observed behaviour of the archive, not a wish.
 */
export type LabelUsage = "used" | "required" | "ignored";

/** What a field type actually supports, and what it does with `label` */
export interface FieldTypeCapabilities {
  labelUsage: LabelUsage;
  minLength: boolean;
  maxLength: boolean;
  min: boolean;
  max: boolean;
  defaultValue: boolean;
  options: boolean;
  optionsSource: boolean;
  multiple: boolean;
  /** bindLabel + bindValue + search */
  remoteSearch: boolean;
  /** The back-end refuses requiredWhen on DateNotAvailable */
  requiredWhen: boolean;
}

const CAPABILITIES: { [type: string]: FieldTypeCapabilities } = {
  String: {
    labelUsage: "used",
    minLength: true,
    maxLength: true,
    min: false,
    max: false,
    defaultValue: true,
    options: false,
    optionsSource: false,
    multiple: false,
    remoteSearch: false,
    requiredWhen: true,
  },
  /**
   * Same as String plus a height (`rows`).
   *
   * The entry exists so the RENDERER treats it properly: without it `capabilitiesOf` falls back on
   * the empty capabilities, and a `requiredWhen` declared on a multi line field would be dropped
   * in silence.
   *
   * The height itself is not declared as a capability yet, because nothing offers it: `Textarea`
   * is deliberately absent from FIELD_TYPES until the editor grows the "rows" control.
   */
  Textarea: {
    labelUsage: "used",
    minLength: true,
    maxLength: true,
    min: false,
    max: false,
    defaultValue: true,
    options: false,
    optionsSource: false,
    multiple: false,
    remoteSearch: false,
    requiredWhen: true,
  },
  Number: {
    labelUsage: "ignored",
    minLength: false,
    maxLength: false,
    min: true,
    max: true,
    defaultValue: true,
    options: false,
    optionsSource: false,
    multiple: false,
    remoteSearch: false,
    requiredWhen: true,
  },
  Date: {
    labelUsage: "required",
    minLength: false,
    maxLength: false,
    min: false,
    max: false,
    defaultValue: false,
    options: false,
    optionsSource: false,
    multiple: false,
    remoteSearch: false,
    requiredWhen: true,
  },
  DateNotAvailable: {
    labelUsage: "required",
    minLength: false,
    maxLength: false,
    min: false,
    max: false,
    defaultValue: false,
    options: false,
    optionsSource: false,
    multiple: false,
    remoteSearch: false,
    requiredWhen: false,
  },
  Select: {
    labelUsage: "ignored",
    minLength: false,
    maxLength: false,
    min: false,
    max: false,
    defaultValue: false,
    options: true,
    optionsSource: false,
    multiple: false,
    remoteSearch: false,
    requiredWhen: true,
  },
  SelectNew: {
    labelUsage: "used",
    minLength: false,
    maxLength: false,
    min: false,
    max: false,
    defaultValue: false,
    options: false,
    optionsSource: true,
    multiple: true,
    remoteSearch: false,
    requiredWhen: true,
  },
  Hidden: {
    labelUsage: "ignored",
    minLength: false,
    maxLength: false,
    min: false,
    max: false,
    defaultValue: true,
    options: false,
    optionsSource: false,
    multiple: false,
    remoteSearch: false,
    requiredWhen: true,
  },
  "Autocomplete-Supplier": {
    labelUsage: "used",
    minLength: false,
    maxLength: false,
    min: false,
    max: false,
    defaultValue: false,
    options: false,
    optionsSource: false,
    multiple: true,
    remoteSearch: true,
    requiredWhen: true,
  },
};

/** Fallback for a type the catalogue does not know: show nothing optional */
const NO_CAPABILITIES: FieldTypeCapabilities = {
  labelUsage: "used",
  minLength: false,
  maxLength: false,
  min: false,
  max: false,
  defaultValue: false,
  options: false,
  optionsSource: false,
  multiple: false,
  remoteSearch: false,
  requiredWhen: false,
};

/**
 * What the given field type supports. Drives the editor: an attribute that makes no
 * sense is not hidden behind a warning, it is simply not offered, which is what makes
 * "options XOR optionsSource" impossible to violate instead of merely validated.
 *
 * @since 3.0.0
 */
export function capabilitiesOf(type: string): FieldTypeCapabilities {
  return CAPABILITIES[type] ? CAPABILITIES[type] : NO_CAPABILITIES;
}

// END - Capability map

// BEGIN - i18n key catalogue

/**
 * Flatten a ngx-translate translations object into its dotted keys.
 *
 * The store already holds the object the app addresses ('properties.detail…'), so no
 * prefix has to be added or stripped here.
 *
 * @since 3.0.0
 */
export function flattenTranslationKeys(translations: any): string[] {
  const keys: string[] = [];
  const walk = (node: any, prefix: string): void => {
    if (!node || typeof node !== "object") {
      return;
    }
    Object.keys(node).forEach((key) => {
      const path = prefix ? `${prefix}.${key}` : key;
      const value = node[key];
      if (value && typeof value === "object" && !Array.isArray(value)) {
        walk(value, path);
        return;
      }
      if (typeof value === "string") {
        keys.push(path);
      }
    });
  };
  walk(translations, "");
  keys.sort();
  return keys;
}

/**
 * The tokens accepted by `fields[].label`: the leaves living under the archive prefix,
 * with the prefix stripped, because that is what gets stored.
 *
 * @since 3.0.0
 */
export function extractFieldLabelTokens(allKeys: string[]): string[] {
  const tokens: string[] = [];
  (allKeys || []).forEach((key) => {
    if (key.indexOf(FIELD_LABEL_PREFIX) !== 0) {
      return;
    }
    const token = key.substring(FIELD_LABEL_PREFIX.length);
    // Only direct leaves: a nested key would not resolve through the prefix
    if (token && token.indexOf(".") < 0) {
      tokens.push(token);
    }
  });
  return tokens;
}

// END - i18n key catalogue

// BEGIN - Field diff

/** A field whose `name` changed between two versions of the same data type */
export interface RenamedField {
  from: string;
  to: string;
}

/**
 * What editing did to the fields that already existed, matched by `_id`.
 *
 * Both operations orphan the values already archived under the old name: the values
 * keep the old key, stop being displayed and nothing raises an error. This is the
 * input of the confirmation dialog shown before saving.
 *
 * Fields with no `_id` are new: they cannot orphan anything and are ignored.
 *
 * @since 3.0.0
 *
 * @param original Fields as they were loaded
 * @param next     Fields as they are about to be sent
 */
export function diffFields(
  original: DataTypeField[],
  next: DataTypeField[]
): { removed: string[]; renamed: RenamedField[] } {
  const removed: string[] = [];
  const renamed: RenamedField[] = [];
  const before = Array.isArray(original) ? original : [];
  const after = Array.isArray(next) ? next : [];

  const afterById: { [id: string]: DataTypeField } = {};
  after.forEach((field) => {
    if (field && field._id) {
      afterById[field._id.toString()] = field;
    }
  });

  before.forEach((field) => {
    if (!field || !field._id) {
      return;
    }
    const survivor = afterById[field._id.toString()];
    if (!survivor) {
      removed.push(field.name);
      return;
    }
    if (survivor.name !== field.name) {
      renamed.push({ from: field.name, to: survivor.name });
    }
  });

  return { removed, renamed };
}

// END - Field diff

// BEGIN - Back-end error paths

/**
 * One step of an express-validator path: either an object key or an array index.
 *
 * 'fields[2].options[0].value' -> ['fields', 2, 'options', 0, 'value']
 *
 * @since 3.0.0
 */
export function parseErrorPath(path: string): Array<string | number> {
  const segments: Array<string | number> = [];
  if (!path) {
    return segments;
  }
  path.split(".").forEach((chunk) => {
    // 'options[0]' -> key 'options', index 0. Several indexes per chunk are supported.
    const bracket = chunk.indexOf("[");
    if (bracket < 0) {
      if (chunk) {
        segments.push(chunk);
      }
      return;
    }
    const key = chunk.substring(0, bracket);
    if (key) {
      segments.push(key);
    }
    const indexes = chunk.substring(bracket).match(/\[(\d+)\]/g);
    if (!indexes) {
      return;
    }
    indexes.forEach((raw) => {
      segments.push(parseInt(raw.replace(/[[\]]/g, ""), 10));
    });
  });
  return segments;
}

/**
 * Index of the field a back-end error path points at, -1 when it points elsewhere.
 * Used to expand the right (collapsed) row before highlighting the control.
 *
 * @since 3.0.0
 */
export function fieldIndexOfErrorPath(path: string): number {
  const segments = parseErrorPath(path);
  if (segments.length >= 2 && segments[0] === "fields") {
    const index = segments[1];
    return typeof index === "number" ? index : -1;
  }
  return -1;
}

// END - Back-end error paths

// BEGIN - Form rules shared by the renderer and Arturo's confirmation

/**
 * Is a `requiredWhen` rule satisfied by the given values?
 *
 * A value not filled in yet is compared as an empty string, so that `ne`/`nin` do not fire on an
 * untouched form. The comparison is on text.
 *
 * Moved here verbatim from `DataTypePreviewBuilder.evaluateRequiredWhen`, which now calls it, so that
 * Arturo's confirmation checks the same rule the archive's form applies. The back-end applies it too
 * (`document-archive-final-values.util.ts`, `isRequiredWhen`).
 *
 * @since 3.0.0
 *
 * @param rule  The rule of the field
 * @param model The values of the form
 */
export function isDataTypeRequiredWhenMet(rule: DataTypeRequiredWhen, model: any): boolean {
  const raw = model && typeof model[rule.field] !== "undefined"
    ? model[rule.field]
    : "";
  const current = raw === null ? "" : raw.toString();
  const values = Array.isArray(rule.values) ? rule.values : [];

  switch (rule.operator) {
    case "eq":
      return values.length ? current === values[0] : false;
    case "ne":
      return values.length ? current !== values[0] : false;
    case "in":
      return values.indexOf(current) >= 0;
    case "nin":
      return values.indexOf(current) < 0;
    default:
      return false;
  }
}

/**
 * The value `autoRenewalFromDuration` derives from a duration: empty duration gives an empty target,
 * '0a' gives "Senza rinnovo automatico", anything else "Rinnovo automatico". `undefined` means
 * "leave the target alone".
 *
 * The written strings are LITERALS, not i18n keys: that is how they are stored in the already
 * archived documents.
 *
 * Moved here verbatim from `DataTypePreviewBuilder.autoRenewalValue`, which now calls it, so that
 * Arturo's confirmation derives the same value. The back-end derives it too
 * (`document-archive-final-values.util.ts`, `autoRenewalValue`).
 *
 * @since 3.0.0
 */
export function autoRenewalFromDuration(duration: any): string {
  if (typeof duration === "undefined" || duration === null) {
    return undefined;
  }
  if (duration === "") {
    return "";
  }
  if (duration === "0a") {
    return "Senza rinnovo automatico";
  }
  return "Rinnovo automatico";
}

// END - Form rules shared by the renderer and Arturo's confirmation
